import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Radio, Settings, RefreshCw, AlertTriangle, CheckCircle, Clock, Link2 } from 'lucide-react';
import { Card, Badge, Button, Modal } from '../../components/ui';
import { api } from '../../lib/api';

interface OracleConfig {
  id: string;
  name: string;
  type: 'CHAINLINK_POR' | 'CHAINLINK_PRICE' | 'CUSTOM';
  network: string;
  contractAddress: string;
  updateFrequency: number;
  isActive: boolean;
  lastUpdate?: string;
  lastValue?: string;
  status: 'HEALTHY' | 'STALE' | 'ERROR';
}

export function OracleConfiguration() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'CHAINLINK_POR' as const,
    network: 'ETHEREUM',
    contractAddress: '',
    updateFrequency: 3600,
    isActive: true
  });

  const { data: oracles, isLoading } = useQuery({
    queryKey: ['oracle-configs'],
    queryFn: async () => {
      const res = await api.get<OracleConfig[]>('/tokenization/oracles');
      return res.data;
    }
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => api.post('/tokenization/oracles', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['oracle-configs'] });
      setShowModal(false);
      resetForm();
    }
  });

  const refreshMutation = useMutation({
    mutationFn: (id: string) => api.post(`/tokenization/oracles/${id}/refresh`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['oracle-configs'] })
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => 
      api.put(`/tokenization/oracles/${id}`, { isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['oracle-configs'] })
  });

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'CHAINLINK_POR',
      network: 'ETHEREUM',
      contractAddress: '',
      updateFrequency: 3600,
      isActive: true
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'HEALTHY':
        return <Badge variant="success"><CheckCircle className="w-3 h-3 mr-1" />Saudável</Badge>;
      case 'STALE':
        return <Badge variant="warning"><Clock className="w-3 h-3 mr-1" />Desatualizado</Badge>;
      case 'ERROR':
        return <Badge variant="error"><AlertTriangle className="w-3 h-3 mr-1" />Erro</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'CHAINLINK_POR': return 'Chainlink Proof of Reserve';
      case 'CHAINLINK_PRICE': return 'Chainlink Price Feed';
      case 'CUSTOM': return 'Oracle Customizado';
      default: return type;
    }
  };

  const formatFrequency = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}min`;
    return `${Math.floor(seconds / 3600)}h`;
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-surface-800 rounded w-64" />
          <div className="grid grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-48 bg-surface-800 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Configuração de Oracles</h1>
          <p className="text-surface-400">Gerencie os oracles de Proof of Reserve e Price Feeds</p>
        </div>
        <Button onClick={() => setShowModal(true)}>
          <Radio className="w-4 h-4 mr-2" />
          Novo Oracle
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-500/20 rounded-lg">
              <Radio className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <p className="text-surface-400 text-sm">Total de Oracles</p>
              <p className="text-xl font-bold text-surface-100">{oracles?.length || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-surface-400 text-sm">Saudáveis</p>
              <p className="text-xl font-bold text-green-400">
                {oracles?.filter(o => o.status === 'HEALTHY').length || 0}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/20 rounded-lg">
              <Clock className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-surface-400 text-sm">Desatualizados</p>
              <p className="text-xl font-bold text-yellow-400">
                {oracles?.filter(o => o.status === 'STALE').length || 0}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-surface-400 text-sm">Com Erro</p>
              <p className="text-xl font-bold text-red-400">
                {oracles?.filter(o => o.status === 'ERROR').length || 0}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Oracle List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {oracles?.map((oracle) => (
          <Card key={oracle.id} className="p-4">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${oracle.isActive ? 'bg-primary-500/20' : 'bg-surface-700'}`}>
                  <Radio className={`w-5 h-5 ${oracle.isActive ? 'text-primary-400' : 'text-surface-500'}`} />
                </div>
                <div>
                  <h3 className="font-semibold text-surface-100">{oracle.name}</h3>
                  <p className="text-sm text-surface-400">{getTypeLabel(oracle.type)}</p>
                </div>
              </div>
              {getStatusBadge(oracle.status)}
            </div>

            <div className="space-y-2 text-sm mb-4">
              <div className="flex items-center justify-between">
                <span className="text-surface-400">Rede</span>
                <Badge variant="info">{oracle.network}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-surface-400">Contrato</span>
                <span className="text-surface-300 font-mono text-xs">
                  {oracle.contractAddress.slice(0, 10)}...{oracle.contractAddress.slice(-8)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-surface-400">Frequência</span>
                <span className="text-surface-300">{formatFrequency(oracle.updateFrequency)}</span>
              </div>
              {oracle.lastUpdate && (
                <div className="flex items-center justify-between">
                  <span className="text-surface-400">Última Atualização</span>
                  <span className="text-surface-300">
                    {new Date(oracle.lastUpdate).toLocaleString('pt-BR')}
                  </span>
                </div>
              )}
              {oracle.lastValue && (
                <div className="flex items-center justify-between">
                  <span className="text-surface-400">Último Valor</span>
                  <span className="text-surface-100 font-semibold">{oracle.lastValue}</span>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => refreshMutation.mutate(oracle.id)}
                disabled={refreshMutation.isPending}
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
                Atualizar
              </Button>
              <Button
                variant={oracle.isActive ? 'ghost' : 'primary'}
                size="sm"
                onClick={() => toggleMutation.mutate({ id: oracle.id, isActive: !oracle.isActive })}
              >
                {oracle.isActive ? 'Desativar' : 'Ativar'}
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {(!oracles || oracles.length === 0) && (
        <Card className="p-12 text-center">
          <Radio className="w-12 h-12 text-surface-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-surface-300 mb-2">Nenhum oracle configurado</h3>
          <p className="text-surface-500 mb-4">Configure oracles para Proof of Reserve e Price Feeds</p>
          <Button onClick={() => setShowModal(true)}>Configurar Oracle</Button>
        </Card>
      )}

      {/* Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); resetForm(); }}
        title="Novo Oracle"
      >
        <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate(formData); }} className="space-y-4">
          <div>
            <label className="block text-sm text-surface-400 mb-1">Nome</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
              placeholder="Ex: Chainlink PoR - USDC"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-surface-400 mb-1">Tipo</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
              className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
            >
              <option value="CHAINLINK_POR">Chainlink Proof of Reserve</option>
              <option value="CHAINLINK_PRICE">Chainlink Price Feed</option>
              <option value="CUSTOM">Oracle Customizado</option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-surface-400 mb-1">Rede</label>
            <select
              value={formData.network}
              onChange={(e) => setFormData({ ...formData, network: e.target.value })}
              className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
            >
              <option value="ETHEREUM">Ethereum</option>
              <option value="POLYGON">Polygon</option>
              <option value="BSC">BSC</option>
              <option value="ARBITRUM">Arbitrum</option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-surface-400 mb-1">Endereço do Contrato</label>
            <input
              type="text"
              value={formData.contractAddress}
              onChange={(e) => setFormData({ ...formData, contractAddress: e.target.value })}
              className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100 font-mono"
              placeholder="0x..."
              required
            />
          </div>

          <div>
            <label className="block text-sm text-surface-400 mb-1">Frequência de Atualização (segundos)</label>
            <input
              type="number"
              value={formData.updateFrequency}
              onChange={(e) => setFormData({ ...formData, updateFrequency: Number(e.target.value) })}
              className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
              min={60}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="ghost" onClick={() => { setShowModal(false); resetForm(); }}>
              Cancelar
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              Criar Oracle
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
